import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useSendActivityForWorkflows } from 'modules/workflowIndex/hooks/useSendActivityForWorkflows';
import { selectSendActivity } from 'modules/workflowIndex/selectors';
import { getInProgressVisitorCount, getWorkflow, selectNodes } from 'modules/workflows/ducks';
import { WorkflowType } from 'modules/workflows/enums';
import { Node } from 'modules/workflows/types';
import { useWorkflowContext } from 'modules/workflows/context';
import { CountPanel, CountText, CountValue, InProgressValue, VisitorCountText } from '../styled';
import { ContactCount } from './ContactCounts.constants';
import { ContactCountItem } from './ContactCounts.types';
import { getContactCounts } from './ContactCounts.utils';
import { BreakdownType } from '../../takeovers/JourneyVisitorBreakdownTakeover/JourneyVisitorBreakdown.constant';

export const CONTACT_COUNTS_TEST_ID = 'contact-counts';
export const IN_PROGRESS_VALUE_TEST_ID = `${ContactCount.InProgress}-count`;

interface ContactCountsProps {
  workflowId: number;
  workflowType: WorkflowType;
  selectedNode?: Node;
  openJourneyVisitorBreakdown: (breakdownType: BreakdownType, numUserTotal?: number) => () => void;
}

export const ContactCounts = ({
  workflowId,
  workflowType,
  openJourneyVisitorBreakdown,
}: ContactCountsProps) => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const hasJVTEvents = (workflow?.visitorsEntered || 0) > 0;
  const { setShowExitSlideover } = useWorkflowContext();

  useSendActivityForWorkflows([{ id: workflowId, workflowType }]);

  const sendActivity = useSelector(selectSendActivity(workflowId));

  const inProgressCount = useSelector(getInProgressVisitorCount) ?? 0;

  const handleOpenExitRulesDetails = () => {
    dispatch(selectNodes([]));
    setShowExitSlideover(true);
  };

  const analytics = useMemo(
    () =>
      getContactCounts(
        sendActivity,
        handleOpenExitRulesDetails,
        inProgressCount,
        openJourneyVisitorBreakdown,
      ),
    [sendActivity, openJourneyVisitorBreakdown, inProgressCount],
  );

  return (
    <CountPanel data-test={CONTACT_COUNTS_TEST_ID}>
      {analytics.map((count: ContactCountItem) => {
        const hasOnClick = typeof count.onClick === 'function' && hasJVTEvents;
        return (
          <VisitorCountText key={count.key}>
            <CountText>{count.text}</CountText>
            {count.key === ContactCount.InProgress ? (
              <InProgressValue as={'div'} data-test={IN_PROGRESS_VALUE_TEST_ID}>
                <b data-test={count.key}>{count.value}</b>
              </InProgressValue>
            ) : (
              <CountValue
                data-test={count.key}
                hasOnClick={hasOnClick}
                onClick={() => hasJVTEvents && count.onClick && count.onClick()}
              >
                {count.value}
              </CountValue>
            )}
          </VisitorCountText>
        );
      })}
    </CountPanel>
  );
};
